import React, { type MouseEventHandler } from 'react'
import { createPortal } from 'react-dom'
import TagBadge from '@components/common/TagBadge/TagBadge.component'
import type { NoteContextType } from '@dataTypes/note.type'
import './NotesSaveNoteModal.css'

type NotesSaveNoteModalProps = {
  show: boolean
  noteName: string
  tag: NoteContextType | string
  isSaving?: boolean
  onDiscard: () => void
  onSave: () => void
}

/** Notes save confirm portal — bypasses DS Modal Overlay stacking. */
const NotesSaveNoteModal: React.FC<NotesSaveNoteModalProps> = ({
  show,
  noteName,
  tag,
  isSaving = false,
  onDiscard,
  onSave,
}) => {
  const name = noteName.trim() || 'this note'

  // Use mousedown: NoteEditor listens on document mousedown (capture) and can
  // re-render before click fires, which drops onClick on portaled buttons.
  const handleSaveMouseDown: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.preventDefault()
    event.stopPropagation()
    if (isSaving) return
    onSave()
  }

  const handleDiscardMouseDown: MouseEventHandler<HTMLButtonElement> = (
    event,
  ) => {
    event.preventDefault()
    event.stopPropagation()
    onDiscard()
  }

  if (!show || typeof document === 'undefined') {
    return null
  }

  return createPortal(
    <div className="notes-save-confirm" role="presentation">
      <div className="notes-save-confirm__scrim" aria-hidden="true" />
      <div
        className="notes-save-confirm__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="notes-save-confirm-title"
      >
        <header className="notes-save-confirm__header">
          <h2
            id="notes-save-confirm-title"
            className="notes-save-confirm__title"
          >
            Saving new note
          </h2>
          <button
            type="button"
            className="notes-save-confirm__close"
            aria-label="Close"
            disabled={isSaving}
            onMouseDown={handleDiscardMouseDown}
          >
            ×
          </button>
        </header>
        <div className="notes-save-confirm__body">
          <p className="notes-save-confirm__description">
            {`Are you sure you want to save the note as "${name}"?`}
          </p>
        </div>
        <footer className="notes-save-confirm__footer">
          <div className="notes-save-confirm__tag">
            <span className="notes-save-confirm__tag-label">Tagged as</span>
            <TagBadge tag={tag} />
          </div>
          <div className="notes-save-confirm__actions">
            <button
              type="button"
              className="notes-save-confirm__btn notes-save-confirm__btn--secondary"
              onMouseDown={handleDiscardMouseDown}
            >
              Discard
            </button>
            <button
              type="button"
              className="notes-save-confirm__btn notes-save-confirm__btn--primary"
              disabled={isSaving}
              onMouseDown={handleSaveMouseDown}
            >
              {isSaving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </footer>
      </div>
    </div>,
    document.body,
  )
}

export default NotesSaveNoteModal
